import {
  format as formatDate,
  parseISO,
  parse,
  startOfDay,
  endOfDay,
  isValid,
  addDays,
  setHours,
  setMinutes,
  isAfter,
  setSeconds,
  startOfWeek,
  addWeeks,
} from 'date-fns';
import { DateFormats } from 'infrastructure/enums';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import type { DateRange } from 'infrastructure/types';

interface IDateFormatterProps {
  date?: string | Date;
  format?: DateFormats;
  toUTC?: boolean;
}

export const DateFormatter = (props: IDateFormatterProps) => {
  const { date, format = DateFormats['MM-dd-yyyy'] } = props;
  if (!date) return '';

  const localDate = typeof date === 'string' ? parseISO(date) : date;

  if (props?.toUTC && isValid(localDate)) {
    const utcDate = utcToZonedTime(localDate, 'UTC');
    return formatDate(utcDate, format);
  }

  if (isValid(localDate)) return formatDate(localDate, format);

  return `${date}`;
};

export const startOfDate = (date: Date) => startOfDay(date);
export const endOfDate = (date: Date) => endOfDay(date);

export const prepareDateRange = (range: DateRange) => {
  return [startOfDate(range[0]), endOfDate(range[1])];
};

export const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getLocalTimeFromTimezoneOffset = (timezoneOffset: number) => {
  const now = new Date();
  const localTimezoneOffset = now.getTimezoneOffset();
  const targetTimezoneOffset = timezoneOffset / 60;
  const totalOffset = (targetTimezoneOffset + localTimezoneOffset) * 60 * 1000;
  return new Date(now.getTime() + totalOffset);
};

export const convertDateToTimeZone = (
  date: Date | string,
  targetTimeZone: string | null,
): Date => {
  if (!targetTimeZone) return typeof date === 'string' ? new Date(date) : date;
  const targetTime = utcToZonedTime(date, targetTimeZone);
  return targetTime;
};

export const convertTimeZoneToUtc = (
  zonedDate: Date | string,
  sourceTimeZone: string,
): Date => {
  const utcDate = zonedTimeToUtc(zonedDate, sourceTimeZone);
  return utcDate;
};

type AvailabilitySlot = {
  day: number;
  startTime: string;
};

export const generateAvailabilityDatesNext2Week = (
  slots: AvailabilitySlot[],
): Date[] => {
  const now = new Date();
  const result: Date[] = [];

  for (let week = 0; week < 2; week++) {
    const weekStart = startOfWeek(addWeeks(now, week), { weekStartsOn: 0 });

    for (const slot of slots) {
      const [hours, minutes] = slot.startTime.split(':').map(Number);

      const slotDate = setMinutes(
        setHours(addDays(weekStart, slot.day), hours),
        minutes,
      );

      if (isAfter(slotDate, now)) {
        result.push(slotDate);
      }
    }
  }
  return result;
};

export const convertTime = (time?: string) => {
  if (!time) return time;
  const currentDate = new Date();
  const [hours, minutes] = time.split(':').map(Number);

  const date = setHours(setMinutes(setSeconds(currentDate, 0), minutes), hours);

  return DateFormatter({
    date,
    format: DateFormats['h:mm a'],
  });
};

export const convertTimeToDate = (time: string): Date => {
  const currentDate = new Date();
  const [hours, minutes] = time.split(':').map(Number);

  const targetDate = setHours(
    setMinutes(setSeconds(currentDate, 0), minutes),
    hours,
  );

  return targetDate;
};

export const convertDateToTime = (date: Date) => {
  return DateFormatter({
    date,
    format: DateFormats['h:mm a'],
  });
};

export const convertToDate = (
  dateString: string,
  formatString: DateFormats,
): Date => {
  const parsedDate = parse(dateString, formatString, new Date());

  if (Number.isNaN(parsedDate.getTime())) {
    throw new Error('Incorrect date format');
  }

  return parsedDate;
};

export const changeFormat = (
  date: string,
  formatFrom: DateFormats,
  formatTo: DateFormats,
) => {
  const dateParsed = convertToDate(date, formatFrom);
  if (isValid(dateParsed)) return formatDate(dateParsed, formatTo);
};

export const parseDateStringWithoutTimeZoneAdopt = (
  dateStr?: string | null,
) => {
  if (!dateStr) return undefined;

  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;

  if (!isoRegex.test(dateStr)) return undefined;

  const cleanDate = dateStr.replace(/Z$/, '');

  const [datePart, timePart] = cleanDate.split('T');
  if (!datePart || !timePart) return undefined;

  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, secondWithMs] = timePart.split(':');
  if (!hour || !minute || !secondWithMs) return undefined;

  const [second, millisecond = '0'] = secondWithMs.split('.');

  return new Date(
    year,
    month - 1,
    day,
    Number(hour),
    Number(minute),
    Number(second),
    Number(millisecond),
  );
};

export const startOfUTC = (date: Date) => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
  );
};

export const endOfUTC = (date: Date) => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999,
    ),
  );
};
